import { useLoaderData } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { differenceBy, findIndex } from 'lodash';
import s from '@components/Tables/KeywordsTable/KeywordsTable.module.scss';
import { Flex, Title } from '@tremor/react';
import { useKeywordsStore } from '@/stores/keywordsStore';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { KeywordFromStore, TagsWithUserPermissions } from '@/types/types';
import { KeywordsService } from '@/services/dataforseo/keywordsService';
import { SearchBar } from '@/components/layouts/Inputs/SearchBar/SearchBar';
import { OutlinedButton } from '@/components/layouts/Buttons/OutlinedButton/OutlinedButton';
import {
  KeywordsTable,
  TableKeyword,
} from '@/components/Tables/KeywordsTable/KeywordsTable';
import { AddKeywordsModal } from '@/components/layouts/Modals/AddKeywordsModal';
import { AddIcon } from '@/assets';

interface Props {
  locationCode: number;
}

export const KeywordsList = ({ locationCode }: Props) => {
  const keywords = useKeywordsStore((state) => state.keywords);
  const setKeywords = useKeywordsStore((state) => state.setKeywords);
  const totalItems = useKeywordsStore((state) => state.totalItems);
  const setTotalItems = useKeywordsStore((state) => state.setTotalItems);
  const [searchValue, setSearchValue] = useState('');
  const { tags } = useLoaderData() as TagsWithUserPermissions;
  const [isOpen, setIsOpen] = useState(false);
  const workspaceId = useWorkspaceIdFromParams() as string;
  const setPage = useKeywordsStore((state) => state.setPage);
  const setIsSearching = useKeywordsStore((state) => state.setIsSearching);
  const setSearchTotalItems = useKeywordsStore((state) => state.setSearchTotalItems);
  const tableKeywords: TableKeyword[] = keywords.map((keyword) => {
    const assignedTags = keyword.tags.map((tagId) => {
      const tag = tags.find((t) => t.id === tagId);
      return { label: tag?.name || 'No tag found', id: tagId };
    });
    return {
      keyword: keyword?.keyword,
      assignedTags,
      language: keyword?.language,
      id: keyword?.id,
    };
  });

  const displayTableKeywords = useMemo(() => {
    return searchValue.length === 0
      ? tableKeywords
      : tableKeywords.filter(({ keyword }) =>
          keyword.toLowerCase().includes(searchValue.toLowerCase()),
        );
  }, [searchValue, tableKeywords]);

  useEffect(() => {
    const fetchFirstKeywords = async () => {
      const keywordsData = await KeywordsService.getFirstPage(workspaceId, 'dataforseo');

      const keywordsCount = await KeywordsService.getTotalCount(
        workspaceId,
        'dataforseo',
      );

      setTotalItems(keywordsCount.data().count);
      setKeywords(keywordsData);
    };
    fetchFirstKeywords().catch((e) => console.error(e));
  }, [setKeywords, setTotalItems, workspaceId]);

  const changeLanguageHandler = useCallback(
    async (language: string, id: number) => {
      const keywordId = keywords[id].id;
      const keywordToChangeIndex = findIndex(keywords, ['id', keywordId]);
      const oldLanguage = keywords[keywordToChangeIndex].language;

      const keywordsCopy = [...keywords];
      keywordsCopy[keywordToChangeIndex].language = language;
      setKeywords((keywords) => {
        const keywordsCopy = [...keywords];
        keywordsCopy[keywordToChangeIndex].language = language;
        return keywordsCopy;
      });
      try {
        await KeywordsService.changeLanguage(
          language,
          keywordId,
          workspaceId,
          'dataforseo',
        );
      } catch (e) {
        setKeywords((keywords) => {
          const keywordsCopy = [...keywords];
          keywordsCopy[keywordToChangeIndex].language = oldLanguage;
          return keywordsCopy;
        });
      }
    },
    [keywords, setKeywords, workspaceId],
  );

  const deleteKeyword = useCallback(
    async (rowId: number) => {
      const keywordToDeleteId = displayTableKeywords[rowId].id;
      const keywordToDelete = keywords.find(
        (k) => k.id === keywordToDeleteId,
      ) as KeywordFromStore;
      const newKeywords = differenceBy(keywords, [keywordToDelete], 'id');
      setKeywords(newKeywords);
      try {
        await KeywordsService.deleteKeyword(
          keywordToDelete.id,
          workspaceId,
          'dataforseo',
        );
      } catch (e) {
        setKeywords((keywords) => {
          const keywordsCopy = [...keywords];
          keywordsCopy.splice(rowId, 0, keywordToDelete);
          return keywordsCopy;
        });
      }
    },
    [displayTableKeywords, keywords, setKeywords, workspaceId],
  );

  const handleDeleteKeyword = useCallback(
    async (rowId: number) => {
      await deleteKeyword(rowId);
    },
    [deleteKeyword],
  );
  const deleteSelectedKeywords = useCallback(
    async (selectedKeywords: Record<number, boolean>) => {
      const keywordsIds = Object.entries(selectedKeywords)
        .filter(([, isSelected]) => isSelected)
        .map(([rowId]) => {
          const keywordToDeleteId = displayTableKeywords[parseInt(rowId)].id;
          const keywordToDelete = keywords.find(
            (k) => k.id === keywordToDeleteId,
          ) as KeywordFromStore;
          return { id: keywordToDelete.id };
        });
      const keywordsCopy = [...keywords];
      const newKeywords = differenceBy(keywords, keywordsIds, 'id');
      setKeywords(newKeywords);
      try {
        await KeywordsService.deleteKeywords(keywordsIds, workspaceId, 'dataforseo');
      } catch (e) {
        setKeywords(keywordsCopy);
      }
    },
    [displayTableKeywords, keywords, setKeywords, workspaceId],
  );

  const handleDeleteSelectedKeywords = useCallback(
    async (selectedKeywords: Record<number, boolean>) => {
      if (Object.keys(selectedKeywords).length === 0) return;
      await deleteSelectedKeywords(selectedKeywords);
    },
    [deleteSelectedKeywords],
  );

  const handleDeleteTag = useCallback(
    async (rowId: number, tagId: string) => {
      const keywordToDeleteTagFrom = keywords[rowId];
      const oldTags = keywordToDeleteTagFrom.tags;
      const newTags = differenceBy(keywordToDeleteTagFrom.tags, [tagId]);
      setKeywords((keywords) => {
        const newKeywords = [...keywords];
        newKeywords[rowId].tags = newTags;
        return newKeywords;
      });
      try {
        await KeywordsService.deleteTag(
          tagId,
          keywordToDeleteTagFrom.id,
          workspaceId,
          'dataforseo',
        );
      } catch (e) {
        setKeywords((keywords) => {
          const newKeywords = [...keywords];
          newKeywords[rowId].tags = oldTags;
          return newKeywords;
        });
      }
    },
    [keywords, setKeywords, workspaceId],
  );

  const onSearchSubmit = async (value: string) => {
    if (!keywords[keywords.length - 1]) {
      const allKeywords = await KeywordsService.getAllKeywords(workspaceId, 'dataforseo');
      setKeywords(allKeywords);
    }
    setSearchTotalItems(
      keywords.filter(({ keyword }) =>
        keyword.toLowerCase().includes(value.toLowerCase()),
      ).length,
    );
    setPage(1);
    setIsSearching(value.length !== 0);
    setSearchValue(value);
  };

  return (
    <div style={{ width: '100%' }}>
      <Flex className={s.titleContainer}>
        <Title className={s.title}>Keywords Management</Title>
        <SearchBar onSubmit={onSearchSubmit} placeholder="Find Keyword" />
      </Flex>
      <section className={s.upperOptions}>
        <OutlinedButton
          icon={AddIcon}
          iconAltText="add icon"
          className={s.addKeywordButton}
          onClick={() => setIsOpen(true)}
        >
          Add Keyword
        </OutlinedButton>
        <AddKeywordsModal
          isOnlyFirstPageFetched={!displayTableKeywords[displayTableKeywords.length - 1]}
          isOpen={isOpen}
          setClose={() => setIsOpen(false)}
          tags={tags}
          locationCode={locationCode}
        />
      </section>
      <KeywordsTable
        searchValue={searchValue}
        totalItems={totalItems}
        tags={tags}
        keywords={displayTableKeywords}
        changeLanguageHandler={changeLanguageHandler}
        handleDeleteKeyword={handleDeleteKeyword}
        handleDeleteTag={handleDeleteTag}
        handleDeleteSelectedKeywords={handleDeleteSelectedKeywords}
      />
    </div>
  );
};
